/**
 * prescribedLine.ts — THE ONE PLACE THE PORTAL ANSWERS "WHAT DOES THIS
 * PRESCRIBED LINE SAY?"
 *
 * Owner repo: @thegpservice/gpsclinics-portal. Slice: prescribed medication
 * truth (read only — nothing here writes, logs or events).
 *
 * ───────────────────────────────────────────────────────────────────────────
 * THE SAFETY INVARIANT
 *   No surface may render a medication NAME, STRENGTH, FORMULATION, DIRECTIONS
 *   or QUANTITY that no prescriber and no formulary supplied. Absent is a
 *   RENDERABLE state, not a gap to be filled.
 * ───────────────────────────────────────────────────────────────────────────
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * WHY THERE IS EXACTLY ONE OF THESE (2026-09-13).
 *
 * Until today the portal had TWO answers to that question and they DISAGREED
 * about the field that decides what goes in a patient's hand:
 *
 *   - this module, which treats a stored `medicationName` EQUAL to the
 *     `productKey` as the machine default it is, and flags the line; and
 *   - a private copy inside `clinicians/ReviewView.tsx`, which fell back to
 *     `medicationName` UNCONDITIONALLY and therefore printed the raw product
 *     key in the Medication row as though a prescriber had chosen it.
 *
 * That second one was the founding defect of this programme reproduced inside
 * the screen built to remove it. There were two more half-answers beside it —
 * `lib/prescribedQuantity.ts` and `lib/prescribedDirections.ts` — each correct
 * on its own field and each a separate thing to keep in step. All three are
 * deleted; everything they did is below.
 *
 * THE RULE THAT FOLLOWS FROM IT: a surface may choose LAYOUT (a table cell, a
 * definition list, a drawer row) but never VALUES. Every value a staff surface
 * prints for a prescribed line comes out of `PrescribedLine`, and the only way
 * to build a `PrescribedLine` is one of the two adapters at the bottom of this
 * file. Add a third wire shape → add a third adapter here, never a rule there.
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * WHERE EACH FIELD IS ALLOWED TO COME FROM (contracts v0.14.0 draws the same
 * line, and orders-service's `resolveMedicationLine` enforces it server-side):
 *
 *   PRODUCT facts     — `name`, `formulation`. From the FORMULARY VARIANT the
 *                       clinician picked, or from a name the wire states that is
 *                       demonstrably not the machine default, carried whole.
 *                       Never composed here out of parts, never title-cased out
 *                       of a product key and presented as a dispensing name.
 *   PRESCRIBING facts — `strength`, `directions`. From the variant the
 *                       prescriber chose, or the prescriber's own words. Never
 *                       from a price catalogue, never inferred.
 *   SERVER WORDING    — `displayQuantity`. Chosen once, server-side, and
 *                       rendered verbatim.
 *
 * ⚠️ THERE IS NO PARSING IN THIS MODULE, AND THERE MUST NEVER BE.
 *
 * The dispensing console used to manufacture its Strength column by
 * regex-splitting the product NAME (`parseMedicationStrength`, deleted by this
 * same lane — see the tombstone in `pharmacy/OrderDetailPanel.tsx`). Runtime
 * parsing of a clinical value is the root cause of the incident this estate is
 * still cleaning up: a PRICING ANCHOR (the dearest variant a family is priced
 * against) was printed as the clinical strength on a SIGNED prescription, where
 * the patient was titrating four steps below it. A four-fold overstatement of a
 * POM strength that no human had ever chosen.
 *
 * So: `strength` is read from the strength field and from nowhere else — the
 * contract calls that "the ONLY field any surface may read a strength from" —
 * and a line with no variant renders a flagged "Not recorded", which is the
 * true answer. See `resolveLine` for the field-by-field rules.
 *
 * ⚠️ R28 — NO MEDICINE NAME APPEARS IN THIS FILE OR ITS SPEC, AND NONE MAY BE
 * ADDED. The repo's own guard (`journey/r28.spec.tsx`) scans every file under
 * `src/`, comments and test fixtures included. A POM name written in as an
 * illustration is how twelve violations entered this repo on 2026-09-11, and a
 * fixture is not an exception: the scan does not care why the string is there.
 * Everything here is described by its ROLE, and the spec reuses the neutral
 * placeholders the repo already has (`Treatment A`, `testing/fixtures.ts`).
 *
 * R19: pure mapping over data the caller already holds. Nothing is logged.
 */
import type { PharmacyPrescriptionItem, PrescribedItem } from '../api/types';

/**
 * The words every staff surface shows for a field nobody recorded.
 *
 * ONE string, exported, so the flag reads identically in the clinician review,
 * the admin drill-down, the deal drawer and the dispensing console. A second
 * sentinel invented per call site is how "Not recorded" and "—" and "" came to
 * mean the same thing in four different places.
 */
export const NOT_RECORDED = 'Not recorded';

/**
 * LEGACY SENTINELS THAT WERE PERSISTED INTO CLINICAL DATA COLUMNS.
 *
 * Before the orders-side fix, `deriveMonth1Rx` wrote the literal string
 * "Not recorded" into `dosage`, and the catalogue wrote `[PLACEHOLDER …]`
 * markers into names. There is no `prescription.update` path anywhere in this
 * estate — those rows are WRITE-ONCE and can never be repaired in the database.
 * They can only ever be re-RENDERED honestly, and that is only possible if the
 * render treats the sentinel as ABSENT rather than as text a clinician typed.
 *
 * The staff projection still round-trips absence through the sentinel today
 * (orders `toContractPrescriptionItem` renders `null` back to the string
 * "Not recorded", because the pinned staff schema cannot express null), so this
 * check is load-bearing on live data and not merely a legacy sweep: without it
 * the portal prints the placeholder unflagged, in the same style as real
 * directions.
 */
function isRecordedValue(value: string | null | undefined): boolean {
  if (value == null) return false;
  const trimmed = value.trim();
  if (trimmed === '') return false;
  if (trimmed.includes('[PLACEHOLDER')) return false;
  if (trimmed.toLowerCase() === NOT_RECORDED.toLowerCase()) return false;
  return true;
}

/**
 * A recorded value VERBATIM (trimmed), or `null` when nothing was genuinely
 * recorded. The single gate every field below passes through.
 */
export function recordedText(value: string | null | undefined): string | null {
  return isRecordedValue(value) ? (value as string).trim() : null;
}

/**
 * The directions to SEED AN EDITOR with — a prescriber's own words, or nothing.
 *
 * Absorbed from the deleted `lib/prescribedDirections.ts`, because "is this
 * text something a human wrote?" must have exactly one answer in this repo and
 * `recordedText` already is it.
 *
 * ⚠️ WHY THE SEED MATTERS MORE THAN THE RENDER. A renderer that printed the
 * persisted sentinel merely looks silly. A form that SEEDED it into the
 * Directions box would invite a clinician to press save and persist the words
 * "Not recorded" onto a new, correctly-strengthed, SIGNED prescription —
 * turning a visible gap into something that looks like a prescriber's
 * instruction. The box starts empty instead, and the form will not submit until
 * real directions are written.
 */
export function seedDirections(dosage: string | null | undefined): string {
  return recordedText(dosage) ?? '';
}

/**
 * One prescribed line as staff read it.
 *
 * Every clinical field is nullable BY DESIGN, not by accident: a `null` here
 * means "no one recorded this", and the renderer is REQUIRED to show it as a
 * visibly flagged absence. There is no shape of this object that carries a
 * strength, a name, a formulation or a quantity that nobody chose.
 */
export interface PrescribedLine {
  /**
   * The internal plan/product key the order was placed against. Always
   * truthful — but it is an ops token, never a dispensing name, so a surface
   * shows it LABELLED as a product key and never in place of `name`.
   *
   * `null` on the dispensing wire, which carries no such FIELD. That is not the
   * same as the console having no key: the queue row the drawer opens from
   * carries one (`itemLabel`), and it is what the name rule is checked against
   * — see `resolvePharmacyLine`. It is not surfaced here because the console
   * shows it already, in the Item column the pharmacist just read.
   */
  productKey: string | null;
  /** PRODUCT FACT — the full licensed dispensing name. `null` → flagged absent. */
  name: string | null;
  /** PRESCRIBING FACT — the strength alone. `null` → flagged absent. */
  strength: string | null;
  /** PRODUCT FACT — the dosage form. `null` → flagged absent. */
  formulation: string | null;
  /** PRESCRIBING FACT — the prescriber's own directions, VERBATIM. `null` → flagged absent. */
  directions: string | null;
  /**
   * WHAT A SURFACE PRINTS FOR THE QUANTITY — the SERVER's own wording where
   * there is one, the bare recorded integer where there is not, and `null` only
   * when no quantity was recorded at all.
   *
   * ⚠️ NEVER COMPOSED HERE. See `quantityText` for the whole argument.
   */
  quantityText: string | null;
  /**
   * The machine-readable pack count, untouched, for a caller that needs to
   * COUNT rather than to display. `null` when the wire sent no usable number.
   *
   * ⚠️ IT IS NOT A RENDERING INPUT. Nothing may word it, suffix it or multiply
   * it — `quantityText` above is the only thing a surface prints.
   */
  quantity: number | null;
}

/**
 * WHAT THE SURFACE PRINTS FOR A PRESCRIBED QUANTITY — and why the browser gets
 * no say in it. (Absorbed from the deleted `lib/prescribedQuantity.ts`.)
 *
 * One prescription was being stated three different ways at once. The signed
 * prescription and the Pharmacy Express dispensing email both word the quantity
 * through ONE orders-side seam (`ports/medication-line.ts#displayQuantity`),
 * which states it as PACKS — "3 packs". The portal went nowhere near that seam:
 * the dispensing console printed a bare `{item.quantity}` under a column headed
 * "Qty", and the clinician's own record of the SAME prescription printed a bare
 * `String(item.quantity)`. A unitless "3" beside a multi-device description
 * reads as three devices, and whether that is what was meant is an OPEN
 * question.
 *
 * ⚠️ THE OBVIOUS FIX IS THE WRONG ONE. Appending " packs" in the browser makes
 * the portal a SECOND formatter of a clinical quantity, sitting beside the
 * server's, agreeing with it only by coincidence and only until one of them
 * changes — the same shape as the strength regex this programme deleted. So
 * the server's string is rendered VERBATIM and nothing is composed here.
 *
 * ⚠️ AND NOTHING IS MULTIPLIED. `quantity × packQty` printed a three-month
 * supply as a unit count nobody recorded — a four-fold UNDER-statement of a POM
 * quantity on a signed document, which is the anchor defect with the sign
 * flipped. There is no pack-size argument to this function and there must not
 * be one. [OPEN — CTO: the packQty/packSize reconciliation. When it lands, a
 * unit count arrives as a SUPPLIED field, never as arithmetic here.]
 *
 * THE FALLBACK IS THE BARE INTEGER, deliberately. An orders build that predates
 * `displayQuantity` still answers these reads during a rolling deploy, and the
 * only alternatives are to invent the unit (forbidden above) or to hide a
 * recorded number behind a "Not recorded" flag — which would be FALSE. The
 * quantity IS recorded; it is its UNIT the portal cannot vouch for. The flag is
 * reserved for the one case where nothing was recorded at all.
 */
function quantityText(displayQuantity: string | null | undefined, quantity: number | null): string | null {
  const served = recordedText(displayQuantity);
  if (served !== null) return served;
  return quantity === null ? null : String(quantity);
}

/**
 * THE WIRE-SHAPE-FREE INPUT TO THE ONE RULE.
 *
 * Deliberately NOT one of the API types. Two different reads describe the same
 * clinical line — the staff prescriptions read (`PrescribedItem`, a stored row
 * plus the server's resolved `variant`) and the dispensing read
 * (`PharmacyPrescriptionItem`, already flattened by orders) — and if the rule
 * below took either of them directly, the other surface would need a rule of
 * its own. That is precisely how the portal came to have two.
 *
 * So the ADAPTERS know about wire shapes and the RULE does not.
 */
interface PrescribedLineSource {
  /** The plan key to SHOW, where the wire carries one. */
  productKey: string | null;
  /**
   * THE MACHINE DEFAULT THIS LINE'S NAME MUST NOT MERELY REPEAT.
   *
   * Deliberately a SEPARATE field from `productKey` above, because the two
   * wires answer "what is the key?" from different places and only one of them
   * also has a key worth SHOWING:
   *
   *   · the staff wire carries `productKey` on the row itself, and it is both
   *     the thing to display and the thing to compare against;
   *   · the dispensing wire carries no key field at all — the key reaches the
   *     console on the QUEUE ROW the drawer was opened from (`itemLabel`), so
   *     there is a token to compare against and nothing new to display.
   *
   * Collapsing the two into one field is what made this rule unenforceable on
   * the dispensing wire: `productKey` was `null` there, so `storedName !==
   * productKey` was true of every name and the strict rule never fired.
   *
   * `null`/`undefined` means the caller has no token — the rule then cannot
   * fire, which is the honest outcome and never a licence to invent one.
   */
  machineDefaultName: string | null | undefined;
  /**
   * A name THE SERVER RESOLVED AND THE PORTAL CAN VOUCH FOR — today, only the
   * formulary variant's licensed description on the staff wire. Trusted as a
   * product fact, because a variant is a thing a prescriber chose.
   *
   * ⚠️ A DISPENSING `name` DOES NOT COME THROUGH HERE. See `resolvePharmacyLine`.
   */
  resolvedName: string | null | undefined;
  /**
   * The name the wire states for this line WITHOUT the portal being able to
   * tell, from the value alone, that a human chose it. ⚠️ Not trusted on its
   * own: the deleted auto-issue lane persisted the raw `productKey` here, and
   * the orders-side catalogue resolution falls back to that same key when the
   * lookup fails. See the rule in `resolveLine`.
   */
  storedName: string | null | undefined;
  strength: string | null | undefined;
  formulation: string | null | undefined;
  directions: string | null | undefined;
  displayQuantity: string | null | undefined;
  quantity: number | null | undefined;
}

/**
 * THE ONE RULE. Every prescribed line on every staff surface comes through
 * here.
 *
 * FIELD BY FIELD, and the reason for each:
 *
 * `name` — prefers the SERVER-RESOLVED name: the FULL licensed dispensing name
 *   carried whole from the formulary variant the prescriber chose. That is the
 *   identical string the signed prescription carries, which is the whole point:
 *   the surfaces must not be able to disagree about one prescription.
 *
 *   Falling back, a wire-stated name is used ONLY WHEN IT IS A NAME A HUMAN
 *   ACTUALLY CHOSE — i.e. when it DIFFERS from the machine default this line
 *   would otherwise carry. When it IS that default (the auto-issue lane's raw
 *   product key, which is how most prescriptions on this estate actually came
 *   into existence — and what an unenriched or catalogue-failed dispensing
 *   projection puts on the wire too) there is no dispensing name on file, so
 *   this is `null` and the surface flags it.
 *
 *   ⚠️ THIS IS THE STRICT RULE, AND IT IS THE POINT OF THE MODULE. We
 *   deliberately do NOT print the key, and do NOT title-case it and print that:
 *   a cosmetic transform of an internal token is not a licensed name, and
 *   dressing one as the other is the same class of mistake as the pricing
 *   anchor. The key is still shown to the reader, labelled as the key.
 *
 * `strength` — the strength field and nothing else. NOT parsed out of `name`,
 *   NOT taken from a price catalogue, NOT inferred from `directions`. (Orders
 *   may read a dose out of the prescriber's OWN directions server-side, because
 *   there the source is words a clinician typed; the portal is downstream of
 *   that decision and must not make a second, differing one.)
 *
 * `formulation` — the formulation field and nothing else.
 *
 * `directions` — the prescriber's own text verbatim, with the persisted
 *   "Not recorded" / "[PLACEHOLDER" sentinels treated as absent.
 *
 * `quantity` — the stored number when it is a real finite number; `null`
 *   otherwise, so a missing quantity never renders as a confident 0.
 *
 * `quantityText` — the server's own wording, else the bare integer, else the
 *   flag. See `quantityText` above; nothing about it is composed here.
 */
function resolveLine(source: PrescribedLineSource): PrescribedLine {
  const storedName = recordedText(source.storedName);
  // A stored name equal to the product key is the machine default, not a choice.
  // Compared against `machineDefaultName`, NOT `productKey`: the dispensing wire
  // has the second and not the first, and this is the one line of the module the
  // console's whole defect turned on.
  const prescriberChoseName =
    storedName !== null && storedName !== recordedText(source.machineDefaultName);
  const quantity =
    typeof source.quantity === 'number' && Number.isFinite(source.quantity) ? source.quantity : null;

  return {
    productKey: recordedText(source.productKey),
    name: recordedText(source.resolvedName) ?? (prescriberChoseName ? storedName : null),
    strength: recordedText(source.strength),
    formulation: recordedText(source.formulation),
    directions: recordedText(source.directions),
    quantityText: quantityText(source.displayQuantity, quantity),
    quantity,
  };
}

/**
 * ADAPTER — the STAFF prescriptions read (`GET
 * /v1/orders/:orderCaseId/prescriptions` and the admin drill-downs).
 *
 * `variant` is the server's snapshot of the variant the prescriber CHOSE, and
 * it is absent on every response today (orders does not project it yet) and
 * absent forever on the rows that predate the formulary. Absent means "flag
 * it", never "fill it in".
 */
export function resolvePrescribedLine(item: PrescribedItem): PrescribedLine {
  return resolveLine({
    productKey: item.productKey,
    // Same value in both roles here: the staff row states its own key, so the
    // thing shown and the thing compared against are one and the same.
    machineDefaultName: item.productKey,
    resolvedName: item.variant?.name,
    storedName: item.medicationName,
    strength: item.variant?.strength,
    formulation: item.variant?.formulation,
    directions: item.dosage,
    displayQuantity: item.displayQuantity,
    quantity: item.quantity,
  });
}

/** Resolve a whole prescription's items. `null`/empty in → empty array out. */
export function resolvePrescribedLines(items: PrescribedItem[] | null | undefined): PrescribedLine[] {
  if (!items || items.length === 0) return [];
  return items.map(resolvePrescribedLine);
}

/**
 * ADAPTER — the DISPENSING read (`GET /v1/orders/pharmacy/:orderCaseId`).
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * ⚠️ `item.name` IS NOT TRUSTED AS A RESOLVED PRODUCT FACT, AND THE DOC THAT
 * SAID IT WAS DESCRIBED A SERVER THAT IS NOT DEPLOYED (2026-09-13).
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * This adapter used to pass `resolvedName: item.name` with `storedName: null`
 * and `productKey: null`, on the stated premise that "name arrives already
 * resolved from the catalogue". TWO REAL ORDERS BUILDS FALSIFY THAT PREMISE,
 * and one of them is the build on `dev`:
 *
 *   · ORDERS `dev` (contracts v0.9.0) DOES NO CATALOGUE LOOKUP AT ALL. Its
 *     `mapPrescriptionItems` is literally `name: item.medicationName` — the RAW
 *     STORED KEY on every auto-issued row. So the console printed the product
 *     key as the medication, in bold, in the Medication column, on the one
 *     screen a pharmacist picks stock from: the founding defect of this
 *     programme, reproduced on the screen built to remove it.
 *   · THE PRESCRIBING BRANCH (contracts v0.14.0) DOES resolve — and still falls
 *     back to the key. `resolveMedicationLine` ends
 *     `detail?.medicationName ?? (stored || item.productKey)`, so a catalogue
 *     row that was renamed, withdrawn, or simply unreachable (the prices read is
 *     fail-soft) puts the key on the wire exactly as `dev` does.
 *
 * A portal deploy and an orders deploy are separate events, so the portal has to
 * be right against BOTH. The rule is therefore applied to the dispensing wire
 * rather than waived on it: `name` goes in as `storedName`, not `resolvedName`,
 * and a name that is merely the machine default is flagged like any other.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * WHERE THE KEY COMES FROM, GIVEN THAT THIS WIRE CARRIES NO `productKey`.
 *
 * From the QUEUE ROW the drawer was opened from — `pharmacyOrderSummary.itemLabel`
 * — which the caller passes in. Both orders builds derive it from the same line,
 * character for character:
 *
 *     itemLabel: r.productKey ?? '—'      (pharmacy.service.ts, dev AND branch)
 *
 * so on both builds it IS the product key, and the comparison catches the real
 * defect. But the rule does not DEPEND on that equivalence to be sound, which is
 * the point of stating it in the console's own terms instead: `itemLabel` is the
 * token the pharmacist has just read in the queue's Item column. If the drawer's
 * Medication cell repeats it back, the console has told them nothing about what
 * to pick — whatever that token happens to be upstream. An absent or unknown
 * label simply means the rule cannot fire; it never licenses a guess.
 *
 * `formulation` prefers the richer catalogue value and falls back to the older
 * dose-form key. Both are catalogue-sourced PRODUCT facts, so preferring one
 * over the other states nothing the catalogue did not; it is not a fallback
 * across the product/prescribing line, which is the kind this module forbids.
 *
 * (`strength` is absent from the `dev` payload entirely — v0.9.0's
 * `pharmacyPrescriptionItem` is `.strict()` and has no such key — so it arrives
 * `undefined` there and `null` on the branch. Both are "nobody recorded one",
 * and `recordedText` already answers them identically.)
 */
export function resolvePharmacyLine(
  item: PharmacyPrescriptionItem,
  /** The queue row's `itemLabel` for this order — see the note above. */
  queueItemLabel: string | null | undefined,
): PrescribedLine {
  return resolveLine({
    // The dispensing wire carries no key FIELD, so there is none to display —
    // and none needs displaying, because the queue behind the drawer shows it.
    productKey: null,
    machineDefaultName: queueItemLabel,
    // ⚠️ NOT `resolvedName`. See the header: neither deployed build guarantees
    // this is a catalogue name, and one of them never even looks.
    resolvedName: null,
    storedName: item.name,
    strength: item.strength,
    formulation: item.formulation ?? item.form,
    directions: item.directions,
    displayQuantity: item.displayQuantity,
    quantity: item.quantity,
  });
}

/* `resolvePharmacyLines` (the plural) IS DELETED (2026-09-13). Nothing called
 * it — the console maps the rows itself because it needs the raw item beside the
 * resolved line for the cold-chain badge — and an exported helper that takes the
 * queue label as a second argument is exactly the thing a future caller would
 * reach for and pass nothing to, quietly re-opening the hole this lane closed.
 * One adapter, one call site, no arity to get wrong. */

/**
 * True when NOTHING clinical was recorded on this line — name, strength,
 * formulation and directions are all absent and only the product key survives.
 *
 * Surfaces use it to add one honest sentence ("only the product key is on
 * file") rather than making the reader infer it from four amber pills in a row.
 *
 * The QUANTITY is deliberately not part of this test: a pack count is a supply
 * fact, not a dispensing identity, and a line that carries one still tells a
 * reader nothing about WHAT was dispensed.
 */
export function isBareProductKey(line: PrescribedLine): boolean {
  return line.name === null && line.strength === null && line.formulation === null && line.directions === null;
}

/**
 * Is either PRESCRIBER-ONLY clinical value missing from this line?
 *
 * The dispensing console raises ONE banner for the whole table on this, rather
 * than leaving a pharmacist to notice an amber pill mid-scan. Deliberately the
 * two PRESCRIBING facts only: a missing formulation is a catalogue lookup gap
 * for the clinic to fix, not a clinical decision a prescriber failed to make,
 * and flattening the two would blunt the banner.
 */
export function missingPrescriberDetail(line: PrescribedLine): boolean {
  return line.strength === null || line.directions === null;
}
